
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PaymentMethod {
  id: string;
  type: 'card' | 'bank';
  last4: string;
  expiry?: string;
  name: string;
  isDefault: boolean;
}

const PaymentMethods: React.FC = () => {
  // Mock data - replace with API call in production
  const paymentMethods: PaymentMethod[] = [
    {
      id: 'card_1',
      type: 'card',
      last4: '4242',
      expiry: '04/25',
      name: 'Visa ending in 4242',
      isDefault: true
    },
    {
      id: 'bank_1',
      type: 'bank',
      last4: '1234',
      name: 'Chase Bank ****1234',
      isDefault: false
    }
  ];

  const handleSetDefault = (id: string) => {
    console.log(`Setting ${id} as default payment method`);
    // In a real app, this would update the default payment method
  };

  const handleRemove = (id: string) => {
    console.log(`Removing payment method ${id}`);
    // In a real app, this would remove the payment method
  };

  const handleAddNew = () => {
    console.log('Adding new payment method');
    // In a real app, this would open a modal to add a new payment method
    alert('In a real app, this would open a form to add a new payment method');
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container max-w-4xl mx-auto px-4">
        <Card className="border-front-gray-200 bg-white shadow-sm">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl font-bold text-front-gray-900">Payment Methods</CardTitle>
            <CardDescription className="text-front-gray-600 mt-2">
              Manage your payment methods for plant care services
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div key={method.id} className="flex items-center justify-between p-6 border border-front-gray-200 rounded-lg bg-front-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-front-gray-100 rounded-full flex items-center justify-center">
                      <CreditCard className="h-6 w-6 text-front-gray-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-front-gray-900">{method.name}</p>
                      {method.expiry && (
                        <p className="text-sm text-front-gray-600">Expires {method.expiry}</p>
                      )}
                      {method.isDefault && (
                        <span className="text-xs bg-front-gray-200 text-front-gray-700 px-2 py-1 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!method.isDefault && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleSetDefault(method.id)}
                        className="border-front-gray-300 text-front-gray-700 hover:bg-front-gray-100"
                      >
                        Set as Default
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleRemove(method.id)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <Button 
                variant="outline" 
                className="w-full mt-6 border-dashed border-front-gray-300 hover:bg-front-gray-50 text-front-gray-700 font-semibold py-6" 
                onClick={handleAddNew}
              >
                <Plus className="mr-2 h-5 w-5" />
                Add Payment Method
              </Button>
            </div>
            
            <div className="mt-8 p-6 bg-front-gray-50 rounded-lg border border-front-gray-200">
              <h3 className="font-semibold text-front-gray-900 mb-3">Payment Security</h3>
              <p className="text-sm text-front-gray-600 leading-relaxed">
                All payment information is encrypted and securely processed through our 
                payment provider. We never store your full card details on our servers.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentMethods;
